import React from 'react';
import { Card, Typography, Tag, Grid, Tooltip, Space, Row, Col, Flex, theme, Switch } from 'antd';
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { useTranslation } from 'react-i18next';
import {
    BookOutlined,
    QuestionCircleOutlined,
    InfoCircleOutlined,
    EditOutlined,
    DeleteOutlined,
    ExpandAltOutlined,
    CopyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';

const { Text, Title, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const EntryContent = ({ entry, screens }) => {
    const { token } = theme.useToken();
    const businessInfoRows = 2;
    const businessInfoFontSize = screens.xs ? 14 : 16;
    const businessInfoLineHeight = 1.35;

    if (entry.type === 'qa') {
        return (
            <Space direction="vertical" size={1} style={{ width: '100%' }}>
                <Flex gap={8} style={{ minWidth: 0 }}>
                    <QuestionCircleOutlined style={{
                        color: token.colorPrimary, // Use primary color from theme
                        marginTop: '4px',
                    }} />
                    <Tooltip title={entry.question}>
                        <Paragraph
                            strong
                            ellipsis={{ rows: screens.xs ? 2 : 1 }}
                            style={{
                                margin: 0,
                                fontSize: screens.xs ? '14px' : '16px',
                                flexGrow: 1,
                                lineHeight: 1.35,
                                minWidth: 0,
                                wordBreak: 'break-word',
                                color: token.colorText // Use text color from theme
                            }}
                        >
                            {entry.question}
                        </Paragraph>
                    </Tooltip>
                </Flex>
                <Paragraph
                    ellipsis={{ rows: screens.xs ? 2 : 1 }}
                    style={{
                        marginInlineStart: '28px',
                        color: token.colorTextSecondary, // Use secondary text color from theme
                        fontSize: screens.xs ? '13px' : '14px',
                        lineHeight: 1.35,
                        wordBreak: 'break-word',
                        marginBottom: 0
                    }}
                >
                    {entry.answer}
                </Paragraph>
            </Space>
        );
    }

    return (
        <Flex gap={8} style={{ minHeight: screens.xs ? 'auto' : '51px', minWidth: 0 }}>
            <InfoCircleOutlined style={{
                color: token.colorSuccess, // Use success color from theme
                marginTop: '8px',
                alignItems: 'flex-start'
            }} />
            <Text
                style={{
                    margin: 0,
                    fontSize: `${businessInfoFontSize}px`,
                    alignItems: 'flex-start',
                    lineHeight: businessInfoLineHeight,
                    minWidth: 0,
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: businessInfoRows,
                    maxHeight: `${businessInfoFontSize * businessInfoLineHeight * businessInfoRows}px`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: token.colorText // Use text color from theme
                }}
            >
                {entry.content}
            </Text>
        </Flex>
    );
};

const KnowledgeCard = ({ item, onExpand, onDelete, onDuplicate, onToggleStatus, className }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const navigate = useNavigate();
    const { token } = theme.useToken(); // Get theme tokens
    const { isDarkMode, toggleDarkMode } = useTheme();

    const maxDisplayEntries = 2;
    const displayedEntries = item.entries.slice(0, maxDisplayEntries);
    const remainingCount = item.totalEntries - maxDisplayEntries;

    const handleEdit = () => {
        navigate(`/knowledge-base/${item.id}`);
    };

    const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [isTogglingStatus, setIsTogglingStatus] = React.useState(false);

    const handleDelete = () => {
        setDeleteModalVisible(true);
    };

    const confirmDelete = async () => {
        try {
            setIsDeleting(true);
            await onDelete(item.id);
        } finally {
            setIsDeleting(false);
            setDeleteModalVisible(false);
        }
    };

    const handleToggleStatus = async () => {
        if (!onToggleStatus || isTogglingStatus) return;
        try {
            setIsTogglingStatus(true);
            await onToggleStatus(item);
        } finally {
            setIsTogglingStatus(false);
        }
    };

    const cardActions = [
        <Tooltip placement="top" title={t('edit')}>
            <EditOutlined key="edit" onClick={handleEdit} style={{ color: token.colorPrimary }} /> {/* Use primary color */}
        </Tooltip>,
        <Tooltip placement="top" title={t('expand')}>
            <ExpandAltOutlined key="expand" onClick={onExpand} style={{ color: token.colorSuccess }} /> {/* Use success color */}
        </Tooltip>,
        <Tooltip placement="top" title={t('duplicate')}>
            <CopyOutlined key="duplicate" onClick={() => onDuplicate(item)} style={{ color: isDarkMode ? '#13c2c2' : '#08979c' }} />
        </Tooltip>,
        <Tooltip placement="top" title={t('delete')}>
            <DeleteOutlined key="delete" onClick={handleDelete} style={{ color: token.colorError }} /> {/* Use error color */}
        </Tooltip>,
    ];

    const disabledContentStyle = item.is_disabled ? { opacity: 0.55 } : {};

    return (
        <>
            <Card
                style={{
                    width: screens.xs ? '100%' : '260px',
                    height: 'fit-content',
                    backgroundColor: token.colorBgContainer, // Use background color from theme
                    border: isDarkMode ? `1px solid ${token.colorBorder}` : 'none', // Use border color from theme
                    transition: 'all 0.3s ease',
                }}
                className={className}
                bordered={false}
                styles={{
                    body: {
                        padding: screens.xs ? '12px' : '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        paddingBottom: 5,
                        height: screens.xs ? 'auto' : '238px',
                        overflow: screens.xs ? 'visible' : 'hidden',
                    }
                }}
                actions={cardActions}
            >
                <Space direction="vertical" size="middle" style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex'
                }}>
                    <Row gutter={8} align="top" wrap={false}>
                        <Col flex="none">
                            <BookOutlined
                                style={{
                                    fontSize: '32px',
                                    color: token.colorPrimary // Use primary color from theme
                                }}
                            />
                        </Col>
                        <Col flex="auto" style={{ minWidth: 0 }}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Tooltip title={item.name}>
                                    <Title
                                        level={4}
                                        ellipsis={{ rows: 1 }}
                                        style={{ margin: 0, color: token.colorText }} // Use text color from theme
                                    >
                                        {item.name}
                                    </Title>
                                </Tooltip>
                                <Paragraph
                                    type="secondary"
                                    ellipsis={{ rows: 1 }}
                                    style={{ marginBottom: 0, height: '22px', color: token.colorTextSecondary }} // Use secondary text color from theme
                                >
                                    {item.description}
                                </Paragraph>
                                <Space size={4} wrap>
                                    <Tag color="blue">{item.qaCount} {t('qa')}</Tag>
                                    <Tag color="green">{item.businessInfoCount} {t('info')}</Tag>
                                    {item.is_disabled && <Tag color="default">{t('disabled')}</Tag>}
                                </Space>
                            </Space>
                        </Col>
                        <Col flex="none">
                            <Tooltip title={item.is_disabled ? t('disabled') : t('enabled')}>
                                <Switch
                                    aria-label={item.is_disabled ? t('disabled') : t('enabled')}
                                    checked={!item.is_disabled}
                                    loading={isTogglingStatus}
                                    onChange={handleToggleStatus}
                                    size="small"
                                />
                            </Tooltip>
                        </Col>
                    </Row>

                    <Space
                        direction="vertical"
                        size={8}
                        style={{
                            width: '100%',
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            ...disabledContentStyle,
                        }}
                    >
                        {displayedEntries.map((entry, index) => (
                            <EntryContent key={index} entry={entry} screens={screens} />
                        ))}

                        {remainingCount > 0 && (
                            <Text
                                type="secondary"
                                style={{
                                    fontSize: screens.xs ? '13px' : '14px',
                                    color: token.colorTextSecondary // Use secondary text color from theme
                                }}
                            >
                                + {remainingCount} {t('more')} {remainingCount === 1 ? t('entry') : t('entries')}
                            </Text>
                        )}
                    </Space>
                </Space>
            </Card>


            <ResponsiveModal
                key="delete-modal"
                title={t('delete_knowledge_base')}
                open={deleteModalVisible}
                onOk={confirmDelete}
                onCancel={() => setDeleteModalVisible(false)}
                okText={t('delete')}
                cancelText={t('cancel')}
                okButtonProps={{ danger: true }}
                confirmLoading={isDeleting}
                desktopWidth={420}
            >
                <div>{t('are_you_sure_you_want_to_delete_knowledge_base', { name: item.name })}</div>
            </ResponsiveModal>
        </>
    );
};

export default KnowledgeCard;
